import * as THREE from 'three';
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';
import FixedWingFlightModel from './FixedWingFlightModel.js';
import FixedWingAIPilot from './FixedWingAIPilot.js';
import HitVolume from '../weapons/HitVolume.js';
import { ENEMIES, FLARES, PLANE_FAILURE, ENEMY_MISSILE } from '../core/SimConfig.js';
import { rand, srand, DEG2RAD } from '../utils.js';

const _emitterWorld = new THREE.Vector3();
const _bodyUp = new THREE.Vector3();
const _launchPos = new THREE.Vector3();
const _launchQuat = new THREE.Quaternion();
const _launchVel = new THREE.Vector3();

/**
 * One AI-controlled fixed-wing aircraft. Parallels EnemyHelicopter — same
 * model-clone + flight model + AI pilot pattern, with a fixed-wing flight
 * model and a pilot that supports an attack-on-player state. Missiles +
 * countermeasures will hook in here later.
 */
export default class EnemyPlane {
  constructor({
    model, params, position, heading, scene,
    particleSystem, explosionSystem = null, audioManager = null, flareSystem = null,
    missileSystem = null,
    initialHealth = 100,
    initialState = 'explore',
  }) {
    this.params = params;
    this.particleSystem = particleSystem;
    this.explosionSystem = explosionSystem;
    this.audioManager = audioManager;
    this.flareSystem = flareSystem;
    this.missileSystem = missileSystem;
    this.health = initialHealth;
    this.dead = false;
    this.paused = true;         // dormant until EnemyManager clears the takeoff grace

    // Audio tag — fixed-wing jets feed the shared distant-aircraft "woosh" loop
    // (AudioManager._updateDistantJets). Helicopters/ground vehicles don't.
    this.isJet = true;

    // Missile magazine — pure bookkeeping; missiles themselves spawn from
    // the missile system at fire time (no pre-mounted models on the plane).
    this.missilesRemaining = ENEMY_MISSILE.magazine;
    this._missileCooldown = 0;

    // RWR tagging — fighters carry both radar and IR-guided missiles.
    // `missileEquipped` reads dynamically so an empty rack stops raising
    // the tracking tone on the player's RWR.
    this.radarEquipped = true;

    // Countermeasure burst state — driven by `onAIM9Launched()` (MAWS-equipped MIG).
    this._flareReactionTimer = 0;
    this._flareBurstRemaining = 0;
    this._flareBurstTimer = 0;

    this.modelRoot = cloneSkeleton(model);
    this.modelRoot.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });

    this.group = new THREE.Group();
    this.group.add(this.modelRoot);
    scene.add(this.group);

    this.flight = new FixedWingFlightModel(params);
    this.flight.position.copy(position);
    this.flight.heading = heading;
    // Seed velocity to match cruise so the spawn doesn't show a stationary jet.
    this.flight.velocity.set(
      -Math.sin(heading) * params.cruiseSpeed,
      0,
      -Math.cos(heading) * params.cruiseSpeed,
    );

    this.pilot = new FixedWingAIPilot(params, initialState);

    // Collision volume
    const hitboxNode = this.modelRoot.getObjectByName('Hitbox');
    if (hitboxNode) {
      if (hitboxNode.isMesh) hitboxNode.visible = false;
      this.hitVolume = new HitVolume(hitboxNode);
    } else {
      this.hitVolume = null;
      console.warn('EnemyPlane: "Hitbox" node not found in model');
    }

    // Damage smoke emitter anchor
    this.emitterNode = this.modelRoot.getObjectByName('Emitter');
    if (!this.emitterNode) {
      console.warn('EnemyPlane: "Emitter" node not found in model');
    }
  }

  update(dt, terrainCollider, player = null) {
    // Dormant during the takeoff grace: hold the spawn pose. Don't integrate the
    // flight model (it would fall / drift forward on its seeded cruise velocity);
    // keep the hit volume live so it stays targetable.
    if (this.paused) {
      this.group.position.copy(this.flight.position);
      this.group.quaternion.copy(this.flight.quaternion);
      if (this.hitVolume) this.hitVolume.update();
      return;
    }

    this._applyDamageEffects(dt);
    this._updateFlareBurst(dt);
    if (this._missileCooldown > 0) this._missileCooldown -= dt;

    if (!this.flight.structuralFailure) {
      this.pilot.update(dt, this.flight, terrainCollider, player, this);
    } else {
      // Small chaos on the roll rate so the spin doesn't look metronomic —
      // it speeds up and slows down a bit as it falls.
      this.flight._failRollRate += srand() * PLANE_FAILURE.rollJitter * dt;
    }
    this.flight.update(dt);
    this.group.position.copy(this.flight.position);
    this.group.quaternion.copy(this.flight.quaternion);

    // Despawn once the wreck is below terrain.
    if (terrainCollider) {
      const ground = terrainCollider.getHeightAt(this.flight.position.x, this.flight.position.z);
      if (ground != null && this.flight.position.y < ground) {
        if (this.particleSystem) {
          this.particleSystem.spawnExplosion(
            this.flight.position.x, ground, this.flight.position.z,
            { size: 2 });
        }
        // Filmed fireball on top of the particle blast — the impact is on the
        // deck, which is the one place the ground-anchored billboard reads
        // right (SimConfig.EXPLOSION_SPRITES). `ground`, not position.y: the
        // wreck is already a little under the terrain when this trips.
        this.explosionSystem?.spawnEvent('aircraftGroundImpact',
          this.flight.position.x, ground, this.flight.position.z);
        if (this.audioManager) {
          this.audioManager.playExplosion(
            'Explosion_Crash',
            this.flight.position.x, ground, this.flight.position.z,
          );
        }
        // EnemyManager turns this into a permanent crash-site fire when it
        // prunes the wreck (fx/GroundFireSystem).
        this.crashSite = { x: this.flight.position.x, y: ground, z: this.flight.position.z };
        this.dead = true;
        return;
      }
    }

    if (this.hitVolume) this.hitVolume.update();

    // Damage smoke trails — exclusive stages, lowest threshold wins.
    if (this.particleSystem && this.emitterNode) {
      this.emitterNode.getWorldPosition(_emitterWorld);
      const v = this.flight.velocity;
      if (this.health < 40) {
        this.particleSystem.spawnFireSmokeTrail(_emitterWorld.x, _emitterWorld.y, _emitterWorld.z, v.x, v.y, v.z);
      } else if (this.health < 60) {
        this.particleSystem.spawnBlackSmokeTrail(_emitterWorld.x, _emitterWorld.y, _emitterWorld.z, v.x, v.y, v.z);
      } else if (this.health < 80) {
        this.particleSystem.spawnWhiteSmokeTrail(_emitterWorld.x, _emitterWorld.y, _emitterWorld.z, v.x, v.y, v.z);
      }
    }
  }

  onProjectileHit(_x, _y, _z, damage = 0, _source = null) {
    this.health = Math.max(0, this.health - damage);
  }

  // ── Missile firing ──────────────────────────────────────────────────────

  /** True when armed: magazine has rounds and cooldown is clear. */
  get canFireMissile() {
    return this.missilesRemaining > 0
      && this._missileCooldown <= 0
      && this.missileSystem != null;
  }

  /** RWR: true while the plane still has missiles on the rack. */
  get missileEquipped() {
    return this.missilesRemaining > 0 && this.missileSystem != null;
  }

  /**
   * Fire one missile at `target` from the plane's current pose. Inherits
   * the plane's inertial velocity. No-op if cooldown or magazine blocks it.
   * Returns true on success.
   */
  tryFireMissileAt(target) {
    if (!this.canFireMissile || !target) return false;
    _launchPos.copy(this.flight.position);
    _launchQuat.copy(this.flight.quaternion);
    _launchVel.copy(this.flight.velocity);
    const ok = this.missileSystem.launch(_launchPos, _launchQuat, _launchVel, target, this);
    if (!ok) return false;
    this.missilesRemaining--;
    this._missileCooldown = ENEMY_MISSILE.salvoCooldown + rand(-ENEMY_MISSILE.salvoCooldownRandom, ENEMY_MISSILE.salvoCooldownRandom);
    return true;
  }

  // ── Countermeasures ──────────────────────────────────────────────────────

  /** Launch warning from WeaponSystem — MIG reacts by dispensing a flare burst. */
  onAIM9Launched() {
    // Don't restart a burst already in flight; the pilot is busy.
    if (this._flareReactionTimer > 0 || this._flareBurstRemaining > 0) return;
    // Damaged aircraft: pilot has bigger problems than countermeasures.
    if (this.health < 50) return;
    const ai = FLARES.ai;
    this._flareReactionTimer = rand(ai.reactionDelay[0], ai.reactionDelay[1]);
    this._flareBurstRemaining = Math.floor(rand(ai.burstCount[0], ai.burstCount[1] + 1));
    this._flareBurstTimer = 0;
  }

  _updateFlareBurst(dt) {
    if (this._flareReactionTimer > 0) {
      this._flareReactionTimer -= dt;
      if (this._flareReactionTimer > 0) return;
    }
    if (this._flareBurstRemaining <= 0) return;
    this._flareBurstTimer -= dt;
    if (this._flareBurstTimer > 0) return;

    this._dispenseFlare();
    this._flareBurstRemaining--;
    this._flareBurstTimer = FLARES.dispenseInterval;
  }

  // One flare ejected straight down (body -up) from the model origin, carrying
  // the aircraft's inertial velocity plus the eject impulse.
  _dispenseFlare() {
    if (!this.flareSystem) return;
    _bodyUp.set(0, 1, 0).applyQuaternion(this.flight.quaternion);
    const speed = FLARES.ejectSpeed;
    const v = this.flight.velocity;
    this.flareSystem.dispense(
      this.flight.position.x, this.flight.position.y, this.flight.position.z,
      v.x - _bodyUp.x * speed,
      v.y - _bodyUp.y * speed,
      v.z - _bodyUp.z * speed,
    );
  }

  /** Same damage pipeline as EnemyHelicopter. */
  _applyDamageEffects(dt) {
    const d = ENEMIES.damage;

    if (this.health < d.decayThreshold && this.health > 0) {
      this.health = Math.max(0, this.health - d.decayRate * dt);
    }

    if (this.health < d.liftLossThreshold) {
      const t = this.health / d.liftLossThreshold;
      this.flight.liftScale = d.liftLossFloor + (1 - d.liftLossFloor) * t;
    } else {
      this.flight.liftScale = 1;
    }

    if (!this.flight.structuralFailure && this.health < d.structuralFailureThreshold) {
      this.flight.structuralFailure = true;
      this.flight.failureGravity = d.failureGravity;

      // Ramp the failure forces in over a random window so the kill reads
      // as the pilot slumping at the controls, not the airframe snapping.
      const f = PLANE_FAILURE;
      const transitionDuration = rand(f.transitionMin, f.transitionMax);
      this.flight._failTransition = 0;
      this.flight._failTransitionRate = 1 / transitionDuration;
      // Carry the current visual roll into the failure accumulator so a
      // banking turn doesn't snap level when control mode flips.
      this.flight._failRoll = this.flight.roll;

      // Seed the body-roll spin: random magnitude across a wide range so
      // some wrecks lazily barrel-roll while others spin violently. Sign is
      // a coin-flip (left vs right roll).
      const rollDir = Math.random() < 0.5 ? -1 : 1;
      this.flight._failRollRate = rollDir * rand(f.rollMin, f.rollMax);

      // Bonus flat-spin: keep the nose up & add a yaw component on top of
      // the roll. Reads as a stalled-out, post-stall departure rather than
      // a clean death-spiral.
      if (Math.random() < f.flatSpinChance) {
        const yawDir = Math.random() < 0.5 ? -1 : 1;
        this.flight._failYawRate = yawDir * rand(f.flatSpinYawMin, f.flatSpinYawMax);
        this.flight._failPitchOffset = f.pitchUpDeg * DEG2RAD;
      }

      if (this.particleSystem) {
        this.particleSystem.spawnExplosion(
          this.flight.position.x, this.flight.position.y, this.flight.position.z,
          { size: 1 });
      }
    }
  }

  dispose() {
    this.group.parent?.remove(this.group);
  }
}
